import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmerchConsentDialog } from '@/components/emerch-consent-dialog';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { SelectPortalContainerContext } from '@/components/ui/select';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  DatasourceInstructionMode,
  ExternalCatalogConnectionCms,
} from '@/lib/graphql/generated/graphql';
import { ProductType } from '@/lib/graphql/generated/graphql';
import { useCreateDealItem } from '@/lib/hooks/useCreateDealItem';
import { useDownloadTemplate } from '@/lib/hooks/useDownloadTemplate';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import {
  type AudienceOption,
  type BuyOption,
  type CguConfig,
  type CumulOption,
  type PerOrderOption,
  type ShippingOption,
  type SubOption,
  type UsageOption,
  buildCouponTerms as buildCouponTermsUtil,
} from '@/lib/utils/coupon-utils';
import { mapProductImportModeToInstructionMode } from '@/lib/utils/instruction-mode-mapper';
import { openImportCouponCodesModalAtom } from '@/states/is-sidebar-alert-open.state';
import { pauseCouponCsvRefetchState } from '@/states/pause-coupon-csv-refetch.state';
import { productsState } from '@/states/products.state';
import { EmerchConsentRequest } from '@/types/emerch-consent';
import Button from '@appchoose/button';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FolderInput } from 'lucide-react';

import { ImportView } from '../import-flow/import-view-props';
import { ViewChooser } from '../import-flow/view-chooser';
import { ViewCouponCodesImport } from '../import-flow/view-coupon-codes-import';
import { ViewCouponsCgu } from '../import-flow/view-coupons-cgu';
import { ViewCouponsImportCsv } from '../import-flow/view-coupons-import-csv';
import { ViewCouponsInstructions } from '../import-flow/view-coupons-instructions';
import { ViewCouponsManual } from '../import-flow/view-coupons-manual';
import { ViewCouponsMenu } from '../import-flow/view-coupons-menu';
import { ViewProductsCopyPreviousSales } from '../import-flow/view-products-copy-previous-sales';
import { ViewProductsImportCsvOptionSelection } from '../import-flow/view-products-import-csv-option-selection';
import { ViewProductsManual } from '../import-flow/view-products-manual';
import { ViewProductsMenu } from '../import-flow/view-products-menu';
import { ViewProductsOptionSelection } from '../import-flow/view-products-option-selection';
import { ViewProductsScraping } from '../import-flow/view-products-scraping';
import { ViewProductsShopify } from '../import-flow/view-products-shopify';
import { ViewInstallShopifyPlugin } from '../import-flow/view-shopify-installation';

function ImportButton() {
  const { t } = useTranslation();
  const {
    deal: { id: dealId, ...deal },
    refetchDeal,
  } = useDealContext();
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();
  const { downloadCouponCodesTemplate, downloadCouponTemplate } =
    useDownloadTemplate();
  const products = useAtomValue(productsState);

  // Determine Shopify plugin availability
  const hasShopifyPlugin =
    (deal.externalCatalogConnections?.filter(
      (connection) =>
        connection.cms === ExternalCatalogConnectionCms.Shopify &&
        connection.isActive
    ).length ?? 0) > 0;
  const canHaveShopifyPlugin =
    (deal.externalCatalogConnections?.filter(
      (connection) =>
        connection.cms === ExternalCatalogConnectionCms.Shopify &&
        !connection.isActive
    ).length ?? 0) > 0;

  // States for dialogs
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [importPortalContainer, setImportPortalContainer] =
    useState<HTMLElement | null>(null);
  const importDialogContentRefCallback = useCallback(
    (node: HTMLElement | null) => setImportPortalContainer(node),
    []
  );
  const [importView, setImportView] = useState<ImportView>('chooser');
  const [couponCodesOpenedViaAlert, setCouponCodesOpenedViaAlert] =
    useState(false);
  const [instructionMode, setInstructionMode] =
    useState<DatasourceInstructionMode>(DatasourceInstructionMode.CreateOnly);
  const [currentFlow, setCurrentFlow] = useState<
    'scraping' | 'copyPreviousSales' | 'shopify' | null
  >(null);
  const [emerchConsentRequest, setEmerchConsentRequest] =
    useState<EmerchConsentRequest | null>(null);

  const handleModifyOption = () => {
    if (currentFlow === 'scraping') {
      setImportView('productsScrapingOptionSelection');
    } else if (currentFlow === 'copyPreviousSales') {
      setImportView('productsCopyPreviousSalesOptionSelection');
    }
  };

  const closeImportDialog = useCallback(() => {
    setImportDialogOpen(false);
    setImportView('chooser');
    setCouponCodesOpenedViaAlert(false);
  }, []);

  const { createItem: createCoupon, loading: creatingCoupon } =
    useCreateDealItem(dealId);
  const { updateProducts } = useUpdateDealItems(dealId);
  const [importedCouponGtins, setImportedCouponGtins] = useState<string[]>([]);
  const [fromCsvImport, setFromCsvImport] = useState(false);
  const [pendingCouponUpdate, setPendingCouponUpdate] = useState<{
    gtins: string[];
    couponTerms: string[];
    couponHowToUse: string[];
  } | null>(null);
  const [, setPauseCouponRefetch] = useAtom(pauseCouponCsvRefetchState);
  const shouldOpenCouponCodesFromAlerts = useAtomValue(
    openImportCouponCodesModalAtom
  );
  const setOpenCouponCodesFromAlerts = useSetAtom(
    openImportCouponCodesModalAtom
  );
  const [couponForm, setCouponForm] = useState<{
    name: string;
    gtin: string;
    listPrice: string;
    choosePrice: string;
    description: string;
    expiration: string;
    experienceDeadline: string;
  }>({
    name: '',
    gtin: '',
    listPrice: '',
    choosePrice: '',
    description: '',
    expiration: '',
    experienceDeadline: '',
  });
  const canCreateCoupon = Boolean(
    couponForm.name.trim() &&
    couponForm.gtin.trim() &&
    couponForm.listPrice.trim() &&
    couponForm.choosePrice.trim() &&
    couponForm.description.trim() &&
    couponForm.expiration.trim() &&
    couponForm.experienceDeadline.trim()
  );

  const [couponTermsTemplate, setCouponTermsTemplate] = useState<string[]>([]);
  const [howToUseTemplate, setHowToUseTemplate] = useState<string[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<
    'simple' | 'service' | 'custom' | null
  >(null);

  const hasDigitalProducts = (products ?? []).some(
    (product) => product.activeVersion.productType === ProductType.Coupon
  );
  const [cgu, setCgu] = useState<CguConfig>({
    audience: {
      include: true,
      option: undefined as AudienceOption | undefined,
    },
    usage: { include: true, option: undefined as UsageOption | undefined },
    buy: { include: true, option: undefined as BuyOption | undefined },
    perOrder: {
      include: true,
      option: undefined as PerOrderOption | undefined,
    },
    cumul: { include: true, option: undefined as CumulOption | undefined },
    subscription: { include: true, option: undefined as SubOption | undefined },
    shipping: {
      include: true,
      option: undefined as ShippingOption | undefined,
    },
    extra: '',
  });

  // Automatically update products when they appe after CSV import
  useEffect(() => {
    if (!pendingCouponUpdate || pendingCouponUpdate.gtins.length === 0) {
      return;
    }

    const toUpdate = products.filter((p) =>
      pendingCouponUpdate.gtins.includes(p.activeVersion.gtin ?? '')
    );

    if (toUpdate.length > 0) {
      updateProducts(
        toUpdate.map((p) => ({
          id: p.activeVersion.id,
          productType: ProductType.Coupon,
          couponTerms: pendingCouponUpdate.couponTerms,
          couponHowToUse: pendingCouponUpdate.couponHowToUse,
        }))
      ).then(() => {
        setPendingCouponUpdate(null);
      });
    }
  }, [products, pendingCouponUpdate, updateProducts]);

  // Panneau Alertes → même modale d’import que le bouton Importer (`openImportCouponCodesModalAtom`).
  useEffect(() => {
    if (!shouldOpenCouponCodesFromAlerts) {
      return;
    }
    if (!products || isOfferLocked) {
      setOpenCouponCodesFromAlerts(false);
      return;
    }
    setCouponCodesOpenedViaAlert(true);
    setImportDialogOpen(true);
    setImportView('couponCodesImport');
    setOpenCouponCodesFromAlerts(false);
  }, [
    shouldOpenCouponCodesFromAlerts,
    setOpenCouponCodesFromAlerts,
    products,
    isOfferLocked,
  ]);

  const buildCouponTerms = (): string[] => {
    return buildCouponTermsUtil({
      cgu,
      experienceDeadline: couponForm.experienceDeadline,
    });
  };
  if (isOfferLocked || !products) {
    return null;
  }

  return (
    <>
      <Dialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          setImportDialogOpen(open);
          if (!open) {
            closeImportDialog();
          }
        }}
      >
        <DialogTrigger asChild>
          <Button
            type="button"
            data-user-guide="Importer"
            appearance="outline"
            data-testid="import-button"
            className="flex items-center gap-2 border border-gray-300 font-medium"
            disabled={isOfferLocked}
          >
            <FolderInput className="h-4 w-4" />
            {t('general.import')}
          </Button>
        </DialogTrigger>
        <DialogContent
          ref={importDialogContentRefCallback}
          className="flex max-h-[85vh] min-h-0 flex-col sm:max-w-[700px]"
        >
          <SelectPortalContainerContext.Provider value={importPortalContainer}>
            {importView === 'installShopifyPlugin' && deal.brandId ? (
              <ViewInstallShopifyPlugin
                setImportView={setImportView}
                brandId={deal.brandId}
                instructionMode={instructionMode}
                refetchDeal={refetchDeal}
                dealId={dealId}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
              />
            ) : importView === 'chooser' ? (
              <ViewChooser
                setImportView={setImportView}
                hasDigitalProducts={hasDigitalProducts}
              />
            ) : importView === 'products' ? (
              <ViewProductsMenu
                setImportView={setImportView}
                isInternalUser={user?.isInternalUser ?? false}
                products={products}
                dealId={dealId}
                onCloseDialog={() => setImportDialogOpen(false)}
                hasShopifyPlugin={hasShopifyPlugin}
                canHaveShopifyPlugin={canHaveShopifyPlugin}
                onEmerchConsentRequested={setEmerchConsentRequest}
              />
            ) : importView === 'productsManual' ? (
              <ViewProductsManual
                setImportView={setImportView}
                dealId={dealId}
                isInternalUser={user?.isInternalUser ?? false}
                onCloseDialog={closeImportDialog}
              />
            ) : importView === 'productsCopyPreviousSales' ? (
              <ViewProductsCopyPreviousSales
                setImportView={setImportView}
                dealId={dealId}
                instructionMode={instructionMode}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
                cameFromOptions={currentFlow === 'copyPreviousSales'}
                products={products}
              />
            ) : importView === 'productsScraping' ? (
              <ViewProductsScraping
                setImportView={setImportView}
                dealId={dealId}
                instructionMode={instructionMode}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
                cameFromOptions={currentFlow === 'scraping'}
              />
            ) : importView === 'productsShopify' ? (
              <ViewProductsShopify
                setImportView={setImportView}
                dealId={dealId}
                instructionMode={instructionMode}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
              />
            ) : importView === 'productsShopifyOptionSelection' ? (
              <ViewProductsOptionSelection
                flowTitle={t(
                  'components.import_flow.products_menu.shopify_import'
                )}
                onNext={(mode) => {
                  const mappedMode =
                    mapProductImportModeToInstructionMode(mode);
                  setInstructionMode(mappedMode);
                  setCurrentFlow('shopify');
                  setImportView('productsShopify');
                }}
                onBack={() => setImportView('products')}
                setImportView={setImportView}
                products={products}
              />
            ) : importView === 'productsScrapingOptionSelection' ? (
              <ViewProductsOptionSelection
                flowTitle={t(
                  'components.import_flow.products_menu.scrape_website'
                )}
                onNext={(mode) => {
                  const mappedMode =
                    mapProductImportModeToInstructionMode(mode);
                  setInstructionMode(mappedMode);
                  setCurrentFlow('scraping');
                  setImportView('productsScraping');
                }}
                onBack={() => setImportView('products')}
                setImportView={setImportView}
                products={products}
              />
            ) : importView === 'productsCopyPreviousSalesOptionSelection' ? (
              <ViewProductsOptionSelection
                flowTitle={t(
                  'components.import_flow.copy_previous_sales.title'
                )}
                onNext={(mode) => {
                  const mappedMode =
                    mapProductImportModeToInstructionMode(mode);
                  setInstructionMode(mappedMode);
                  setCurrentFlow('copyPreviousSales');
                  setImportView('productsCopyPreviousSales');
                }}
                onBack={() => setImportView('products')}
                setImportView={setImportView}
                products={products}
              />
            ) : importView === 'productsImportCSVOptionSelection' ? (
              <ViewProductsImportCsvOptionSelection
                flowTitle={t('general.import_csv_file')}
                dealId={dealId}
                onBack={() => setImportView('products')}
                onUploadSuccess={async () => {
                  closeImportDialog();
                }}
                onCloseDialog={closeImportDialog}
                setImportView={setImportView}
                products={products}
                onEmerchConsentRequested={setEmerchConsentRequest}
              />
            ) : importView === 'coupons' ? (
              <ViewCouponsMenu setImportView={setImportView} />
            ) : importView === 'couponsImport' ? (
              <ViewCouponsImportCsv
                setImportView={setImportView}
                dealId={dealId}
                downloadTemplate={downloadCouponTemplate}
                setImportedCouponGtins={setImportedCouponGtins}
                setFromCsvImport={setFromCsvImport}
                setPauseCouponRefetch={setPauseCouponRefetch}
                products={products}
              />
            ) : importView === 'couponsManual' ? (
              <ViewCouponsManual
                setImportView={setImportView}
                couponForm={couponForm}
                setCouponForm={setCouponForm}
                canCreateCoupon={canCreateCoupon}
                creatingCoupon={creatingCoupon}
              />
            ) : importView === 'couponCodesImport' ? (
              <ViewCouponCodesImport
                setImportView={setImportView}
                dealId={dealId}
                downloadTemplate={downloadCouponCodesTemplate}
                {...(couponCodesOpenedViaAlert
                  ? {
                      onCouponCodesUploadSuccess: closeImportDialog,
                      onCouponCodesBack: closeImportDialog,
                    }
                  : {})}
              />
            ) : importView === 'couponsCGU' ? (
              <ViewCouponsCgu
                setImportView={setImportView}
                cgu={cgu}
                setCgu={setCgu}
                buildCouponTerms={buildCouponTerms}
                setCouponTermsTemplate={setCouponTermsTemplate}
              />
            ) : [
                'instructionsSelect',
                'instructionsBuild',
                'instructionsPreview',
              ].includes(importView) ? (
              <ViewCouponsInstructions
                setImportView={setImportView}
                selectedFlow={selectedFlow}
                setSelectedFlow={setSelectedFlow}
                howToUseTemplate={howToUseTemplate}
                setHowToUseTemplate={setHowToUseTemplate}
                couponForm={couponForm}
                onFinish={async () => {
                  if (fromCsvImport) {
                    setPendingCouponUpdate({
                      gtins: importedCouponGtins,
                      couponTerms: couponTermsTemplate,
                      couponHowToUse: howToUseTemplate,
                    });
                  } else {
                    if (!canCreateCoupon) return;
                    await createCoupon({
                      name: couponForm.name,
                      gtin: couponForm.gtin,
                      listPrice: parseFloat(
                        couponForm.listPrice.replace(',', '.')
                      ),
                      choosePrice: parseFloat(
                        couponForm.choosePrice.replace(',', '.')
                      ),
                      description: couponForm.description,
                      productType: ProductType.Coupon,
                      shippingPrice: 0,
                      couponExpirationDate: new Date(couponForm.expiration),
                      couponTerms: couponTermsTemplate,
                      couponHowToUse: howToUseTemplate,
                      isSelected: true,
                    });
                  }
                  setFromCsvImport(false);
                  setPauseCouponRefetch(false);
                  setImportedCouponGtins([]);
                  closeImportDialog();
                }}
                finishing={creatingCoupon}
                importView={importView}
              />
            ) : (
              <></>
            )}
          </SelectPortalContainerContext.Provider>
        </DialogContent>
      </Dialog>
      <EmerchConsentDialog
        request={emerchConsentRequest}
        onClose={() => setEmerchConsentRequest(null)}
      />
    </>
  );
}

export default ImportButton;
